import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { AuthShell } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function SignUp() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next");
  const safeNext = next && (next.startsWith("/api/oauth/authorize") || next.startsWith("/oauth/consent")) ? next : null;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await signup(name, email, password);
      navigate(safeNext || "/setup", { replace: true });
    } catch (err) {
      setError(apiError(err, "Unable to create your account."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Create your MIA account"
      subtitle="One account. Your devices, your memories, your rules."
      footer={<>Already have an account? <Link to="/login" data-testid="goto-signin-link" className="text-primary hover:underline">Sign in</Link></>}
    >
      <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
        <div className="space-y-2">
          <Label htmlFor="name">Name</Label>
          <Input id="name" required autoComplete="name" data-testid="signup-name-input"
                 value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" required autoComplete="email" data-testid="signup-email-input"
                 value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" required minLength={8} autoComplete="new-password" data-testid="signup-password-input"
                 value={password} onChange={(e) => setPassword(e.target.value)} />
          <p className="text-xs text-muted-foreground">At least 8 characters.</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm">Confirm password</Label>
          <Input id="confirm" type="password" required autoComplete="new-password" data-testid="signup-confirm-input"
                 value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </div>
        {error && <p className="text-sm text-destructive" data-testid="signup-error">{error}</p>}
        <Button type="submit" className="w-full" disabled={loading} data-testid="signup-submit-button">
          {loading ? "Creating account…" : "Create account"}
        </Button>
      </form>
    </AuthShell>
  );
}
